import { module as BaseModule } from "modujs"
import Swiper from "swiper"
import { EffectFade, Keyboard, Navigation, Thumbs } from "swiper/modules"

import "swiper/css"
import "swiper/css/effect-fade"
import "swiper/css/keyboard"
import "swiper/css/navigation"

export default class extends BaseModule {

  init() {
    this.modalGallery = this.el
    this.mainSliderEl = this.modalGallery.querySelector("[data-modal-gallery-main]")
    this.thumbsSliderEl = this.modalGallery.querySelector("[data-modal-gallery-thumbs]")
    
    this.maxZoomLevels = 5
    this.zoomScale = 0.5
    this.currentZoomLevel = 0
    this.mainSwiper = null
    this.thumbsSwiper = null
    this.isInitialized = false
    
    // Check for debug mode (URL parameter or data attribute)
    this.debugMode = this.checkDebugMode()
    
    // Remove hidden classes from images immediately
    this.removeHiddenClasses()
    
    document.addEventListener("clickSliderThumbnail", (event) => {
      this.openModalGallery(event.detail.index)
    })

    this.closeButton = this.modalGallery.querySelector("[data-modal-gallery-close]")
    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => {
        this.closeModalGallery()
      })
    }

    this.zoomInButton = this.modalGallery.querySelector("[data-modal-gallery-zoom-in]")
    if (this.zoomInButton) {
      this.zoomInButton.addEventListener("click", () => {
        this.zoomIn()
      })
    }

    this.zoomOutButton = this.modalGallery.querySelector("[data-modal-gallery-zoom-out]")
    if (this.zoomOutButton) {
      this.zoomOutButton.addEventListener("click", () => {
        this.zoomOut()
      })
    }

    this.updateZoomButtonsState()
    
    // Open modal immediately if debug mode is enabled
    if (this.debugMode) {
      console.log('ModalGallery: Debug mode enabled - opening modal immediately')
      setTimeout(() => {
        this.openModalGallery(0)
      }, 100)
    }
  }

  checkDebugMode() {
    // Check URL parameter
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('debugModal') === 'true' || urlParams.get('debugModal') === '1') {
      return true
    }
    
    // Check data attribute
    if (this.modalGallery.dataset.debugModal === 'true' || this.modalGallery.dataset.debugModal === '1') {
      return true
    }
    
    // Check localStorage (persists across page loads)
    if (localStorage.getItem('modalGalleryDebug') === 'true') {
      return true
    }
    
    return false
  }

  removeHiddenClasses() {
    // Remove hidden classes from all images in the modal
    const images = this.modalGallery.querySelectorAll("img")
    images.forEach(img => {
      img.classList.remove("hidden")
      img.style.display = ""
      img.style.visibility = ""
      img.style.opacity = ""
    })
  }

  initSwipers() {
    if (!this.mainSliderEl || !this.thumbsSliderEl || this.isInitialized) return

    // Initialize thumbnail slider first
    this.thumbsSwiper = new Swiper(this.thumbsSliderEl, {
      modules: [Thumbs],
      loop: true,
      slidesPerView: "auto",
      spaceBetween: 4,
      watchSlidesProgress: true,
      observer: true,
      observeParents: true,
      breakpoints: {
        1024: {
          spaceBetween: 8,
        },
      },
      on: {
        init: () => {
          // Set initial active thumbnail
          this.updateActiveThumb(0)
        },
      },
    })

    // Initialize main slider with fade effect
    this.mainSwiper = new Swiper(this.mainSliderEl, {
      modules: [EffectFade, Keyboard, Navigation, Thumbs],
      loop: true,
      effect: "fade",
      fadeEffect: {
        crossFade: true,
      },
      slidesPerView: 1,
      spaceBetween: 0,
      observer: true,
      observeParents: true,
      thumbs: {
        swiper: this.thumbsSwiper,
      },
      navigation: {
        nextEl: this.modalGallery.querySelector("[data-modal-gallery-next]"),
        prevEl: this.modalGallery.querySelector("[data-modal-gallery-prev]"),
      },
      keyboard: {
        enabled: true,
        onlyInViewport: false,
      },
      on: {
        slideChange: (swiper) => {
          this.resetZoom()
          this.updateActiveThumb(swiper.realIndex)
        },
      },
    })

    // Handle thumbnail clicks - use data attribute for correct index mapping
    const thumbSlides = this.thumbsSliderEl.querySelectorAll(".swiper-slide")
    thumbSlides.forEach((slide) => {
      slide.addEventListener("click", () => {
        const thumbIndex = parseInt(slide.getAttribute("data-modal-gallery-thumb-index"), 10)
        if (!isNaN(thumbIndex)) {
          this.mainSwiper.slideToLoop(thumbIndex)
        }
      })
    })
    
    this.isInitialized = true
  }

  updateActiveThumb(realIndex) {
    const thumbSlides = this.thumbsSliderEl.querySelectorAll(".swiper-slide")
    thumbSlides.forEach((slide) => {
      const thumbIndex = parseInt(slide.getAttribute("data-modal-gallery-thumb-index"), 10)
      if (thumbIndex === realIndex) {
        slide.classList.add("is-active")
      } else {
        slide.classList.remove("is-active")
      }
    })
  }

  openModalGallery(index) {
    document.body.classList.add("has-modal-gallery")
    this.modalGallery.classList.add("is-open")
    
    // Remove hidden classes again when opening
    this.removeHiddenClasses()
    
    // Initialize Swiper if not already initialized
    if (!this.isInitialized) {
      this.initSwipers()
    }
    
    // Wait for modal to be visible, then update and navigate
    setTimeout(() => {
      if (this.mainSwiper && this.thumbsSwiper) {
        this.mainSwiper.update()
        this.thumbsSwiper.update()
        
        // Navigate to the correct slide after update
        this.mainSwiper.slideToLoop(index)
        this.updateActiveThumb(index)
        this.resetZoom()
      }
    }, 50)
  }

  closeModalGallery() {
    if (!this.mainSwiper) return

    document.body.classList.remove("has-modal-gallery")
    this.modalGallery.classList.remove("is-open")
    this.resetZoom()
  }

  getActiveImage() {
    if (!this.mainSwiper) return null
    
    const activeSlide = this.mainSwiper.slides[this.mainSwiper.activeIndex]
    if (!activeSlide) return null
    
    return activeSlide.querySelector("[data-modal-gallery-image]")
  }

  applyZoom(zoomLevel) {
    const activeImage = this.getActiveImage()
    if (!activeImage) return

    const wrapper = activeImage.closest("[data-modal-gallery-image-wrapper]")
    if (!wrapper) return

    const scale = 1 + (zoomLevel * this.zoomScale)
    activeImage.style.transform = `scale(${scale})`
    this.currentZoomLevel = zoomLevel
    this.updateZoomButtonsState()
  }

  resetZoom() {
    this.currentZoomLevel = 0
    const images = this.modalGallery.querySelectorAll("[data-modal-gallery-image]")
    images.forEach(image => {
      image.style.transform = "scale(1)"
    })
    this.updateZoomButtonsState()
  }

  updateZoomButtonsState() {
    if (this.zoomInButton) {
      this.zoomInButton.disabled = this.currentZoomLevel >= this.maxZoomLevels
    }
    if (this.zoomOutButton) {
      this.zoomOutButton.disabled = this.currentZoomLevel <= 0
    }
  }

  zoomIn() {
    if (this.currentZoomLevel < this.maxZoomLevels) {
      this.applyZoom(this.currentZoomLevel + 1)
    }
  }

  zoomOut() {
    if (this.currentZoomLevel > 0) {
      this.applyZoom(this.currentZoomLevel - 1)
    }
  }

  destroy() {
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true)
      this.mainSwiper = null
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true)
      this.thumbsSwiper = null
    }
  }

}
